import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { useGetFile } from '@/hooks/useFiles';
import { DeleteConfirm } from '@/sections/insights/hunts/DeleteConfirm';
import { PaneState } from '@/sections/insights/hunts/PaneState';
import {
  useDeleteHuntMemory,
  useHuntMemory,
  useSaveHuntMemory,
} from '@/sections/insights/hunts/useHunts';
import type { Hunt } from '@/types';
import { formatDate } from '@/utils/date.util';

import { SidebarListItem } from './SidebarListItem';

interface MemoryPaneProps {
  hunt: Hunt;
}

interface MemoryItem {
  title: string;
  updated?: string;
}

export function MemoryPane({ hunt }: MemoryPaneProps) {
  const { data, isLoading } = useHuntMemory(hunt.uuid);
  const [selected, setSelected] = useState<string | null>(null);

  // /my returns MyFile[] (key, name, updated, ...). Derive the memory
  // title from the tail of the stored name so the UI stays title-oriented.
  // Backend namespace is `memory/hunt/<uuid>/` (see
  // services/memory/namespace.go); the summary.log entry lives in the
  // same namespace and is filtered out — it has its own Log tab.
  const prefix = `memory/hunt/${hunt.uuid}/`;
  const items: MemoryItem[] = (data ?? [])
    .map(file => ({
      title: file.name.startsWith(prefix)
        ? file.name.slice(prefix.length)
        : file.name,
      updated: file.updated,
    }))
    .filter(item => item.title !== '' && item.title !== 'summary.log')
    .sort((a, b) => a.title.localeCompare(b.title));

  return (
    <PaneState
      isLoading={isLoading}
      isEmpty={items.length === 0}
      empty={{
        title: 'No memory items yet',
        description:
          'The agent records atomic notes per target or hypothesis as it works.',
      }}
    >
      <div className="flex h-full">
        <div className="bg-chariot-background-secondary/40 w-60 shrink-0 overflow-y-auto">
          <MemoryList
            items={items}
            selected={selected}
            onSelect={setSelected}
          />
        </div>
        <div className="min-w-0 flex-1 overflow-y-auto">
          {selected ? (
            <MemoryEditor key={selected} hunt={hunt} title={selected} />
          ) : (
            <div className="text-chariot-text-secondary p-4 text-sm">
              Select a memory item to view or edit.
            </div>
          )}
        </div>
      </div>
    </PaneState>
  );
}

interface MemoryListProps {
  items: MemoryItem[];
  selected: string | null;
  onSelect: (title: string) => void;
}

function MemoryList({ items, selected, onSelect }: MemoryListProps) {
  return (
    <ul>
      {items.map(item => (
        <SidebarListItem
          key={item.title}
          isSelected={item.title === selected}
          onClick={() => onSelect(item.title)}
        >
          <div className="truncate font-mono font-medium" title={item.title}>
            {item.title}
          </div>
          {item.updated ? (
            <div className="text-chariot-text-secondary mt-1 text-xs">
              {formatDate(item.updated)}
            </div>
          ) : null}
        </SidebarListItem>
      ))}
    </ul>
  );
}

interface MemoryEditorProps {
  hunt: Hunt;
  title: string;
}

function MemoryEditor({ hunt, title }: MemoryEditorProps) {
  const { data, isLoading, isFetching } = useGetFile<string>(
    { name: `memory/hunt/${hunt.uuid}/${title}` },
    { enabled: !!hunt.uuid && !!title }
  );
  const { mutate: save, isPending: saving } = useSaveHuntMemory(hunt.uuid);
  const { mutate: remove, isPending: deleting } = useDeleteHuntMemory(
    hunt.uuid
  );

  const fileContent = typeof data === 'string' ? data : '';
  // The parent keys this component by `title`, so remounting on change
  // resets state naturally — no sync effect needed. `draft` stays null
  // until the user types and we render `fileContent` from useGetFile's
  // cache in the meantime.
  const [draft, setDraft] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const content = draft ?? fileContent;
  const dirty = draft !== null && draft !== fileContent;
  const stillLoading = isLoading || (data === undefined && isFetching);

  return (
    <PaneState
      isLoading={stillLoading}
      isEmpty={!stillLoading && data === undefined}
      empty={{
        title: 'Memory item unavailable',
        description: 'This memory item could not be loaded.',
      }}
    >
      <div className="flex h-full flex-col">
        <div className="border-chariot-stroke flex items-center justify-between border-b p-3">
          <div className="font-mono text-xs" title={title}>
            {title}
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={!dirty || saving}
              onClick={() => save({ title, content: draft ?? '' })}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={deleting}
              onClick={() => setConfirmDeleteOpen(true)}
            >
              Delete
            </Button>
          </div>
        </div>
        <textarea
          value={content}
          onChange={e => setDraft(e.target.value)}
          spellCheck={false}
          className="bg-chariot-background-primary text-chariot-text-primary focus:ring-chariot-brand-primary flex-1 resize-none p-3 font-mono text-xs focus:ring-2 focus:outline-hidden focus:ring-inset"
        />
      </div>
      <DeleteConfirm
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title="Delete memory item?"
        description={
          <>
            Delete <span className="font-mono">{title}</span>? The agent may
            recreate it on the next iteration.
          </>
        }
        confirmLabel="Delete"
        isPending={deleting}
        onConfirm={() =>
          remove({ title }, { onSuccess: () => setConfirmDeleteOpen(false) })
        }
      />
    </PaneState>
  );
}
